import React, { useEffect, useState } from "react";
import { Select, Spin, Empty, Segmented, Table, Button } from "antd";
import { BarChartOutlined, TableOutlined, LineChartOutlined } from "@ant-design/icons";
import ReactApexChart from "react-apexcharts";

import DashboardLayout from "../../layout/dashboardLayout/DashboardLayout";
import OfficeReportPanel from "./OfficeReportPanel";
import * as api from "./controller";
import {
  FAMILY_LABELS, FAMILY_ORDER, NUMERIC,
  formatBucket, sparkColumnOptions, stackedColumnOptions, userBarOptions,
} from "./analytics/chartTheme";
import "./crm.css";
import "./analytics/analytics.css";

const ok = (r) => r && r.status >= 200 && r.status < 300;

const PERIODS = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
];

// The page's organising question. Population is a filter, never a recolour —
// a series keeps its hue whichever view is selected.
const POPULATIONS = [
  { label: "Everyone", value: "all" },
  { label: "FTC team", value: "ftc" },
  { label: "Office staff", value: "office" },
];

// The app is hash-routed (createHashHistory in store/configureStore.js), so the
// query string lives inside the fragment — "#/crm-analytics?office=3", not
// "/crm-analytics?office=3". Reading window.location.search here would always
// find an empty string, and writing to the pathname would drop the fragment and
// navigate away from the page.
function hashQuery() {
  const hash = window.location.hash || "";
  const i = hash.indexOf("?");
  return new URLSearchParams(i === -1 ? "" : hash.slice(i + 1));
}

function readParams() {
  const p = hashQuery();
  return {
    office: p.get("office") ? Number(p.get("office")) : null,
    period: PERIODS.some((x) => x.value === p.get("period")) ? p.get("period") : "daily",
  };
}

function writeParams(next) {
  const p = hashQuery();
  Object.entries(next).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
  const hash = window.location.hash || "#/crm-analytics";
  const path = hash.split("?")[0];
  const qs = p.toString();
  window.history.replaceState(null, "", qs ? `${path}?${qs}` : path);
}

export default function CrmAnalyticsPage() {
  const initial = readParams();
  const [offices, setOffices] = useState([]);
  const [officeId, setOfficeId] = useState(initial.office);
  const [period, setPeriod] = useState(initial.period);
  const [population, setPopulation] = useState("all");
  const [asTable, setAsTable] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await api.getOfficesList();
      if (!cancelled && ok(res)) {
        const list = res.json || [];
        setOffices(list);
        // Land on something rather than an empty page.
        setOfficeId((cur) => cur || (list[0] && list[0].id) || null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Guarded against out-of-order responses: this aggregate runs one grouped
  // query per metric, so switching from a busy office to a quiet one can land
  // the slow first payload *after* the second — permanently, since the spinner
  // is only shown while `data` is still null.
  useEffect(() => {
    if (!officeId) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await api.getOfficeActivity(officeId, { period });
        if (!cancelled) setData(ok(res) ? res.json : null);
      } catch (e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [officeId, period]);
  useEffect(() => { writeParams({ office: officeId, period }); }, [officeId, period]);

  const buckets = (data && data.buckets) || [];
  // Label with the period the DATA was fetched for, not the one just selected —
  // the old chart stays on screen during the refetch, so using `period` drew 30
  // daily columns all labelled with the same month.
  const dataPeriod = (data && data.period) || period;
  const categories = buckets.map((b) => formatBucket(b, dataPeriod));
  const series = (data && data.series) || {};

  // Roll the seven metrics up into their four families for the headline.
  const familySeries = FAMILY_ORDER.map((family) => {
    const members = Object.values(series).filter((s) => s.family === family);
    const points = buckets.map((_, i) =>
      members.reduce((sum, s) => sum + (s.points[i] || 0), 0));
    return { name: FAMILY_LABELS[family], data: points, family };
  }).filter((s) => s.data.some((n) => n > 0) || !data);

  const grandTotal = Object.values(series).reduce((n, s) => n + s.total, 0);
  const byUser = (data && data.by_user) || { ftc: [], office: [], other: [] };

  return (
    <DashboardLayout>
      <div className="crm-page">
        <div className="crm-page-head">
          <div className="crm-page-head-left">
            <span className="crm-page-icon"><BarChartOutlined /></span>
            <div>
              <div className="crm-page-title">Activity</div>
              <div className="crm-page-sub">Who is doing this office&rsquo;s insurance work</div>
            </div>
          </div>
        </div>

        <div className="crm-an-controls">
          <Select
            showSearch
            optionFilterProp="label"
            style={{ minWidth: 260 }}
            placeholder="Choose an office"
            value={officeId}
            onChange={setOfficeId}
            options={offices.map((o) => ({ label: o.name, value: o.id }))}
          />
          <Segmented options={PERIODS} value={period} onChange={setPeriod} />
          <Segmented options={POPULATIONS} value={population} onChange={setPopulation} />
          <Button
            type="text"
            icon={asTable ? <LineChartOutlined /> : <TableOutlined />}
            onClick={() => setAsTable((v) => !v)}
          >
            {asTable ? "Charts" : "Table"}
          </Button>
        </div>

        {loading && !data ? (
          <div className="crm-an-loading"><Spin size="large" /></div>
        ) : !officeId ? (
          <Empty description="Choose an office to see its activity." image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : asTable ? (
          <ActivityTable buckets={buckets} period={dataPeriod} series={series} />
        ) : (
          <>
            <div className="crm-an-card">
              <div className="crm-an-card-head">
                <span className="crm-an-card-title">All activity</span>
                <span className="crm-an-card-total" style={NUMERIC}>{grandTotal}</span>
              </div>
              {grandTotal === 0 ? (
                <Empty description="No activity in this window." image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <ReactApexChart
                  type="bar"
                  height={300}
                  series={familySeries.map((s) => ({ name: s.name, data: s.data }))}
                  options={stackedColumnOptions(categories, familySeries.map((s) => s.family))}
                />
              )}
            </div>

            <div className="crm-an-split">
              <UserPanel title="FTC team" rows={byUser.ftc} family="ftc"
                         dim={population === "office"} />
              <UserPanel title="Office staff" rows={byUser.office} family="office"
                         dim={population === "ftc"} />
            </div>

            {byUser.other && byUser.other.length ? (
              <div className="crm-an-card">
                <div className="crm-an-card-head">
                  <span className="crm-an-card-title">Other users</span>
                </div>
                <div className="crm-sub" style={{ marginBottom: 8 }}>
                  Recorded by accounts that are neither FTC nor office staff — usually a
                  super-admin acting directly.
                </div>
                <UserRows rows={byUser.other} />
              </div>
            ) : null}

            <div className="crm-an-grid">
              {Object.entries(series).map(([key, s]) => (
                <div className="crm-an-tile" key={key}>
                  <div className="crm-an-tile-head">
                    <span className="crm-an-tile-label">{s.label}</span>
                    <span className="crm-an-tile-num" style={NUMERIC}>{s.total}</span>
                  </div>
                  <ReactApexChart
                    type="bar"
                    height={120}
                    series={[{ name: s.label, data: s.points }]}
                    options={sparkColumnOptions(categories, s.family)}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {officeId ? (
          <div className="crm-an-card crm-an-reports">
            <div className="crm-an-card-head">
              <span className="crm-an-card-title">Value report</span>
              <span className="crm-sub">
                What this clinic receives about the work we&rsquo;ve done
              </span>
            </div>
            {/* Same component the office drawer's Reports tab mounts, driven by
                the office Select above — so the whole page is about one clinic. */}
            <OfficeReportPanel officeId={officeId} />
          </div>
        ) : null}
      </div>
    </DashboardLayout>
  );
}

function UserPanel({ title, rows, family, dim }) {
  return (
    <div className={`crm-an-card${dim ? " is-dim" : ""}`}>
      <div className="crm-an-card-head">
        <span className="crm-an-card-title">{title}</span>
        <span className="crm-an-card-total" style={NUMERIC}>
          {rows.reduce((n, r) => n + r.total, 0)}
        </span>
      </div>
      {rows.length ? (
        <ReactApexChart
          type="bar"
          height={Math.max(140, rows.length * 34 + 40)}
          series={[{ name: "Actions", data: rows.map((r) => r.total) }]}
          options={userBarOptions(rows.map((r) => r.name), family)}
        />
      ) : (
        <div className="crm-empty">No activity from this group in this window.</div>
      )}
    </div>
  );
}

function UserRows({ rows }) {
  return (
    <div className="crm-an-userrows">
      {rows.map((r) => (
        <div className="crm-an-userrow" key={r.id}>
          <span className="crm-an-username">{r.name}</span>
          <span className="crm-an-userval" style={NUMERIC}>{r.total}</span>
        </div>
      ))}
    </div>
  );
}

/** The chart data as numbers — identity is never colour-alone. */
function ActivityTable({ buckets, period, series }) {
  const columns = [
    { title: "Metric", dataIndex: "label", fixed: "left", width: 190 },
    ...buckets.map((b, i) => ({
      title: formatBucket(b, period),
      dataIndex: `b${i}`,
      align: "right",
      width: 74,
      render: (v) => <span style={NUMERIC}>{v}</span>,
    })),
    {
      title: "Total", dataIndex: "total", align: "right", fixed: "right", width: 74,
      render: (v) => <strong style={NUMERIC}>{v}</strong>,
    },
  ];
  const rows = Object.entries(series).map(([key, s]) => {
    const row = { key, label: s.label, total: s.total };
    s.points.forEach((n, i) => { row[`b${i}`] = n; });
    return row;
  });
  return (
    <div className="crm-an-card">
      <Table size="small" columns={columns} dataSource={rows} pagination={false} scroll={{ x: "max-content" }} />
    </div>
  );
}
